import * as React from "react"
import * as AvatarPrimitive from "@radix-ui/react-avatar"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "../../../lib/utils"

const avatarVariants = cva(
  "relative flex shrink-0 overflow-hidden rounded-full",
  {
    variants: {
      size: {
        xs: "h-6 w-6 text-2xs",
        sm: "h-8 w-8 text-xs",
        md: "h-10 w-10 text-sm",
        lg: "h-12 w-12 text-base",
        xl: "h-16 w-16 text-lg",
      },
    },
    defaultVariants: {
      size: "md",
    },
  }
)

const avatarFallbackVariants = cva(
  "flex h-full w-full items-center justify-center rounded-full font-[var(--font-family--sans)] font-medium uppercase",
  {
    variants: {
      color: {
        default: "bg-muted text-muted-foreground",
        primary: "bg-primary text-primary-foreground",
        brand: "bg-brand text-white",
        destructive: "bg-destructive text-destructive-foreground",
        neutral: "bg-epic-neutral-3 text-foreground",
      },
    },
    defaultVariants: {
      color: "default",
    },
  }
)

export interface AvatarProps
  extends React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root>,
    VariantProps<typeof avatarVariants> {
  src?: string
  alt?: string
  fallback?: string
  color?: "default" | "primary" | "brand" | "destructive" | "neutral"
}

const Avatar = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Root>,
  AvatarProps
>(({ className, size, src, alt, fallback, color = "default", ...props }, ref) => (
  <AvatarPrimitive.Root
    ref={ref}
    className={cn(avatarVariants({ size }), className)}
    {...props}
  >
    {src && (
      <AvatarPrimitive.Image
        src={src}
        alt={alt ?? fallback ?? "avatar"}
        className="aspect-square h-full w-full object-cover"
      />
    )}
    <AvatarPrimitive.Fallback
      className={cn(avatarFallbackVariants({ color }))}
      delayMs={src ? 600 : 0}
    >
      {fallback ? fallback.slice(0, 2) : "?"}
    </AvatarPrimitive.Fallback>
  </AvatarPrimitive.Root>
))
Avatar.displayName = "Avatar"

export interface AvatarGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  max?: number
  size?: VariantProps<typeof avatarVariants>["size"]
  children: React.ReactNode
}

const AvatarGroup = React.forwardRef<HTMLDivElement, AvatarGroupProps>(
  ({ className, max, size, children, ...props }, ref) => {
    const items = React.Children.toArray(children)
    const visible = max ? items.slice(0, max) : items
    const overflow = max ? items.length - max : 0

    return (
      <div
        ref={ref}
        className={cn("flex items-center", className)}
        {...props}
      >
        {visible.map((child, i) =>
          React.isValidElement(child)
            ? React.cloneElement(child as React.ReactElement<AvatarProps>, {
                size,
                className: cn(
                  (child as React.ReactElement<AvatarProps>).props.className,
                  "ring-2 ring-white",
                  i > 0 && "-ml-2"
                ),
              })
            : child
        )}
        {overflow > 0 && (
          <div
            className={cn(
              avatarVariants({ size }),
              avatarFallbackVariants({ color: "neutral" }),
              "-ml-2 ring-2 ring-white"
            )}
          >
            +{overflow}
          </div>
        )}
      </div>
    )
  }
)
AvatarGroup.displayName = "AvatarGroup"

export { Avatar, AvatarGroup, avatarVariants, avatarFallbackVariants }
